import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useState,
  useEffect,
} from 'react';
import { useDataModel } from '@thd-nucleus/data-sources';
import { getChannelID, redirectToSignIn } from '../utils/general';
import {
  IAlertMessage,
  TAlertStatus,
  TAlertType,
} from '../models/common/alert';
import { b2cProfile } from '../models/graphQLModels';
import { IProfile } from '../models/profile';
import {
  ERR_GENERIC,
  ERR_REDIRECT,
} from '../constants/response-error-messages';
import { getIsAuthError } from '../constants/error-codes';
import { createNewRelicActions, logNewRelicAction } from '../utils/newrelic';

export interface IInstantCheckoutStatusContextValues {
  isEnrolledInInstantCheckout: boolean;
  profile: IProfile | null;
  alertMessage: IAlertMessage | null;
  setAlertMessage: Dispatch<SetStateAction<IAlertMessage | null>>;
  refetchProfile: () => void;
  userId: string;
  customerAccountId: string;
  profileLoading: boolean;
  updateProfileLoading: boolean;
  setUpdateProfileLoading: Dispatch<SetStateAction<boolean>>;
  setProfile: Dispatch<SetStateAction<IProfile | null>>;
  timesLoaded: number;
  setTimesLoaded: Dispatch<SetStateAction<number>>;
}

interface IInstantCheckoutStatusProviderProps {
  children: React.ReactNode;
  userId: string;
  customerAccountId: string;
}

export const InstantCheckoutStatusContext =
  createContext<IInstantCheckoutStatusContextValues | null>(null);

export const InstantCheckoutStatusProvider = ({
  children,
  userId,
  customerAccountId,
}: IInstantCheckoutStatusProviderProps) => {
  const channelId = getChannelID();
  const actions = createNewRelicActions('InstantCheckoutStatusProvider');

  const [alertMessage, setAlertMessage] = useState<IAlertMessage | null>(null);
  const [updateProfileLoading, setUpdateProfileLoading] =
    useState<boolean>(false);
  const [profile, setProfile] = useState<IProfile | null>(null);
  const [timesLoaded, setTimesLoaded] = useState(0);

  const {
    data,
    refetch: refetchProfile,
    loading,
    error,
  } = useDataModel('b2cProfile', {
    variables: {
      userId,
      customerAccountId,
      channelId,
    },
    skip: !userId || !customerAccountId,
    ssr: false,
    context: { withAuth: true },
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    if (data) {
      logNewRelicAction(actions.PROFILE);
      const retrievedProfile: IProfile = {
        customerAccountId: data?.b2cProfile?.customerAccountId,
        userProfile: {
          isEnrolledInInstantCheckout:
            data?.b2cProfile?.userProfile?.isEnrolledInInstantCheckout,
          lastModifiedDate: data?.b2cProfile?.userProfile?.lastModifiedDate,
          name: {
            firstName: data?.b2cProfile?.userProfile?.name?.firstName,
            lastName: data?.b2cProfile?.userProfile?.name?.lastName,
          },
          postalAddress: {
            zipCode: data?.b2cProfile?.userProfile?.postalAddress?.zipCode,
          },
        },
        lastModifiedDate: data?.b2cProfile?.lastModifiedDate,
      };
      if (!data?.b2cProfile?.userProfile?.name) {
        delete retrievedProfile.userProfile.name;
      }
      if (!data?.b2cProfile?.userProfile?.postalAddress) {
        delete retrievedProfile.userProfile.postalAddress;
      }
      setProfile(retrievedProfile);
    }
  }, [data]);

  useEffect(() => {
    if (error) {
      logNewRelicAction(actions.PROFILE, error);
      if (getIsAuthError(error)) {
        setAlertMessage({
          alertType: TAlertType.GENERAL,
          alertStatus: TAlertStatus.INFO,
          text: ERR_REDIRECT.message,
        });
        redirectToSignIn();
      } else {
        setAlertMessage({
          alertType: TAlertType.GENERAL,
          alertStatus: TAlertStatus.ERROR,
          text: ERR_GENERIC.message,
        });
      }
    }
  }, [error]);

  return (
    <InstantCheckoutStatusContext.Provider
      value={{
        isEnrolledInInstantCheckout:
          profile?.userProfile?.isEnrolledInInstantCheckout || false,
        alertMessage,
        setAlertMessage,
        refetchProfile,
        userId,
        customerAccountId,
        profileLoading: loading,
        profile,
        updateProfileLoading,
        setUpdateProfileLoading,
        setProfile,
        timesLoaded,
        setTimesLoaded,
      }}
    >
      {children}
    </InstantCheckoutStatusContext.Provider>
  );
};

InstantCheckoutStatusProvider.dataModel = {
  b2cProfile,
};
